import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiReturnEnterPhoneData,
  type EnrollmentMultiReturnEnterPhoneSubmitInput,
  type EnrollmentMultiReturnEnterPhoneSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi ReturnEnterPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnEnterPhoneData>
 * @example const response = await getEnrollmentMultiReturnEnterPhoneData()
 */
export const getEnrollmentMultiReturnEnterPhoneData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnEnterPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiReturnEnterPhoneData',
      query: `mutation getEnrollmentMultiReturnEnterPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          nextStep
          error
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnEnterPhoneData }
  return response.enterApplication
}

export const useGetEnrollmentMultiReturnEnterPhoneData = createQueryHook<
  undefined,
  EnrollmentMultiReturnEnterPhoneData
>(getEnrollmentMultiReturnEnterPhoneData)

/**
 * Submit Enrollment Multi ReturnEnterPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnEnterPhoneSubmitOutput>
 * @example const response = await submitEnrollmentMultiReturnEnterPhoneData();
 */
export const submitEnrollmentMultiReturnEnterPhoneData = async (
  enrollmentReturnEnterPhoneSubmitInput: EnrollmentMultiReturnEnterPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnEnterPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiReturnEnterPhoneData',
      query: `mutation submitEnrollmentMultiReturnEnterPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentReturnEnterPhoneSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnEnterPhoneSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentMultiReturnEnterPhoneData = createMutationHook<
  EnrollmentMultiReturnEnterPhoneSubmitInput,
  EnrollmentMultiReturnEnterPhoneSubmitOutput
>(submitEnrollmentMultiReturnEnterPhoneData)
